import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Paper, Dialog, DialogTitle, 
    DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { UserInterface, UserChangePasswordInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import usersStore from "../../../store/usersStore";
import userRoleStore from '../../../store/userRolesStore';
import subscriptionPlansStore from '../../../store/subscriptionPlansStore';
import { getUsersTableColumns } from './usersTableColumns';
import UserUpdateDialog from './UserUpdateDialog';

const UsersAdminList: React.FC = () => {
    const [userId, setUserId] = useState('');
    const [users, setUsers] = useState<UserInterface[]>([]);
    const [user, setUser] = useState<UserInterface | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<UserInterface | null>(null); 
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [userToDeactivate, setUserToDeactivate] = useState<UserInterface | null>(null);
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [userToActivate, setUserToActivate] = useState<UserInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [userToUpdate, setUserToUpdate] = useState<UserInterface | null>(null);
    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
    const [userToChangePassword, setUserToChangePassword] = useState<UserChangePasswordInterface | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Activate/Deactivate user
    const handleToggleActive = async (user: UserInterface) => {
    setError(null);
    setSuccess(null);
    try {
        const endpoint = user.active
        ? `/users/inactivateUser/${user.id}`
        : `/users/activateUser/${user.id}`;
        const result = await apiRequest<GenericResponseInterface>(endpoint, 'PUT');
        if (result.status) {
            setSuccess(result.message || 'User status updated successfully');
            handleGetAllUsers(); // Refresh list
        } else {
            setError(result.message || 'Failed to update user status');
        }
    } catch (err: any) {
        setError(err.message || 'Failed to update user status');
    }
    };  

    const handleGetAllUsers = async () => {
    setError(null);
    try {
        await usersStore.loadUsers();
        setUsers(usersStore.users);
        setUser(null);
    } catch (err: any) {
        setError(err.message || 'Failed to fetch users');
    }
    };

    const handleGetUserById = async () => {
    setError(null);
    setSuccess(null);
    if (!userId) return setError('Please enter a user ID');
    try {
        const result = await apiGet<GenericResponseInterface<UserInterface>>(`/users/${userId}`);
        if (result.status && result.data) {
            setUser(result.data);
            setUsers([]);
        } else {
            setError(result.message || 'Failed to fetch user');
        }
    } catch (err: any) {
        setError(err.message || 'Failed to fetch user');
    }
    };

    // Delete user
    const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setError(null);
    setSuccess(null);
    try {
        const result = await apiRequest<GenericResponseInterface>(`/users/delete/${userToDelete.id}`, 'DELETE');
        if (result.status) {
            setSuccess(result.message || 'User deleted successfully');
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            handleGetAllUsers(); // Refresh list
        } else {
            setError(result.message || 'Failed to delete user');
        }
    } catch (err: any) {
        setError(err.message || 'Failed to delete user');
    }
    };

    // update user
    const handleUpdateUser = async () => {
    if (!userToUpdate || !userToUpdate.id) return;
    setError(null);
    setSuccess(null);
    console.log("Updating user with:", JSON.stringify(userToUpdate));
    try {
        const result = await apiRequest<GenericResponseInterface<UserInterface>>(`/users/update/${userToUpdate.id}`, 'PUT', userToUpdate);
        if (result.status) {
            setSuccess(result.message || 'User updated successfully');
            setUpdateDialogOpen(false);
            setUserToUpdate(null);
            handleGetAllUsers(); // Refresh list
        } else {
            setError(result.message || 'Failed to update user');
        }
    } catch (err: any) {
        setError(err.message || 'Failed to update user');
    }
};

const handleUpdateUserPassword = async () => {
  if (!userToChangePassword || !userToChangePassword.userId) return;
  setError(null);
  setSuccess(null);
  try {
    const result = await apiRequest<GenericResponseInterface>(`/users/changePassword/${userToChangePassword.userId}`, 'PUT', userToChangePassword);
    if (result.status) {
        setSuccess(result.message || 'Password updated successfully');
        setChangePasswordDialogOpen(false);
        setUserToChangePassword(null);
        handleGetAllUsers(); // Refresh list
    } else {
        setError(result.message || 'Failed to update user password');
    }
  } catch (err: any) {
    setError(err.message || 'Failed to update user password');
  }
};

    useEffect(() => {
    (async () => {
        await usersStore.loadUsers();
        setUsers(usersStore.users); // set local users state after loading
        await userRoleStore.loadUserRoles(); // needed for update
        await subscriptionPlansStore.loadSubscriptionPlans(); // needed for update
    })();
    }, []); 

    const columns = getUsersTableColumns(
    setUserToDelete,
    setDeleteDialogOpen,
    setUserToDeactivate,
    setDeactivateDialogOpen,
    setUserToActivate,
    setActivateDialogOpen,
    setUserToUpdate,
    setUpdateDialogOpen,
    setUserToChangePassword,
    setChangePasswordDialogOpen
    );

    //const paginationModel = { page: 0, pageSize: 10 };

    return (
    <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleGetAllUsers}>Get All Users</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleGetUserById}>Get User by ID</Button>
            <TextField
                label="User ID"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                size="small"
            />
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
            {/* all users  */}
            {users.length > 0 && (
                <DataGrid
                rows={users.map(u => ({ ...u, id: u.id ?? Math.random() }))}
                columns={columns}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10, page: 0 } }
                }}
                pageSizeOptions={[10, 25, 50]}
                checkboxSelection
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                getRowClassName={params =>
                    params.indexRelativeToCurrentPage % 2 === 0
                    ? 'datagrid-row-even'
                    : 'datagrid-row-odd'
                }
                sx={{
                    '& .datagrid-row-even': { bgcolor: '#f5f5f5' },
                    '& .datagrid-row-odd': { bgcolor: '#e3f2fd' },
                }}
                autoHeight
                />
            )}
        </Paper>

        {/* single user by id */}
        {user && (
        <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
            <DataGrid
            rows={[{ ...user, id: user.id ?? Math.random() }]}
            columns={columns}
            hideFooter
            checkboxSelection={false}
            getRowClassName={() => 'datagrid-row-odd'}
            sx={{
                '& .datagrid-row-odd': { bgcolor: '#e3f2fd' },
            }}
            autoHeight
            />
        </Paper>
        )}

        {/* Delete confirmation dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
                Are you sure you want to delete user{' '}
                <strong>{userToDelete?.name || userToDelete?.username}</strong>?
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button color="error" onClick={handleDeleteUser}>Delete</Button>
            </DialogActions>
        </Dialog>

        {/* Deactivate confirmation dialog */}
        <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
            <DialogTitle>Confirm Deactivation</DialogTitle>
            <DialogContent>
                <Typography>
                Are you sure you want to deactivate user <strong>{userToDeactivate?.name || userToDeactivate?.username}</strong>?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>What will happen:</strong><br />
                - The user and all their AdSpaces, AdSpaceOwnerPackages, and AdSpacePhotos will be deactivated.<br />
                - Ongoing rentals will remain active and renters will be notified.<br />
                - Pending and future rentals will be set inactive and renters notified.<br />
                - Ended rentals will remain inactive.<br />
                - All affected users will receive system notifications.<br />
                <br />
                <strong>This action can affect multiple users and entities. Please confirm you want to proceed.</strong>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
                <Button
                color="warning"
                onClick={async () => {
                    if (userToDeactivate) {
                    await handleToggleActive(userToDeactivate);
                    setDeactivateDialogOpen(false);
                    setUserToDeactivate(null);
                    }
                }}
                >
                Deactivate
                </Button>
            </DialogActions>
        </Dialog>        

        {/* activate confirmation dialog */}
        <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
            <DialogTitle>Confirm Activation</DialogTitle>
            <DialogContent>
                <Typography>
                Are you sure you want to activate user <strong>{userToActivate?.name || userToActivate?.username}</strong>?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>What will happen:</strong><br />
                - The user and all their AdSpaces, AdSpaceOwnerPackages, and AdSpacePhotos will be activated.<br />
                - Ongoing rentals will remain active and renters will be notified.<br />
                - Pending and future rentals that were cancelled will be set active and renters notified.<br />
                - Ended rentals will remain inactive.<br />
                - All affected users will receive system notifications.<br />
                <br />
                <strong>This action can affect multiple users and entities. Please confirm you want to proceed.</strong>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
                <Button
                color="success"
                onClick={async () => {
                    if (userToActivate) {
                    await handleToggleActive(userToActivate);
                    setActivateDialogOpen(false);
                    setUserToActivate(null);
                    }
                }}
                >
                Activate
                </Button>
            </DialogActions>
        </Dialog>

        { /** User Update form Dialog */ }
        <UserUpdateDialog
        open={updateDialogOpen}
        userToUpdate={userToUpdate}
        setUserToUpdate={setUserToUpdate}
        onClose={() => {
            setUpdateDialogOpen(false);
            setSuccess(null);
        }}
        onSave={handleUpdateUser}
        userRoles={userRoleStore.userRoles}
        subscriptionPlans={subscriptionPlansStore.subscriptionPlans}
        updateError={error}
        updateSuccess={success}
        />

        { /** change password form  **/}
        <Dialog open={changePasswordDialogOpen} onClose={() => setChangePasswordDialogOpen(false)}>
            <DialogTitle>Change Password</DialogTitle>
            <DialogContent>
                <TextField
                    label="New Password"
                    type="password"
                    fullWidth
                    value={userToChangePassword?.newPassword || ''}
                    onChange={e => setUserToChangePassword(u => u ? { ...u, newPassword: e.target.value } : u)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setChangePasswordDialogOpen(false)}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={handleUpdateUserPassword}
                >
                    Change Password
                </Button>
            </DialogActions>
        </Dialog>

    </Box>
    );
};

export default UsersAdminList;